import {refreshAnalysis, refreshAnalysisTemplate} from 'analysis-tool/polling/refreshPipeline';
import refreshTemplateSelectorQuery from 'analysis-tool/templates/refreshTemplateSelectorQuery';
import {assert} from 'Asserts';
import navigateToURL from 'lib/navigateToURL';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import notifications from 'notifications';

import * as AnalysisTemplateDAO from './AnalysisTemplateDAO';
import {AnalysisTemplateGraphQL} from './AnalysisTemplateTypes';

export async function applyAnalysisTemplate(
  analysisId: string,
  data: AnalysisTemplateDAO.ApplyAnalysisTemplateParams
): Promise<void> {
  await AnalysisTemplateDAO.applyAnalysisTemplate(analysisId, data);
  await refreshAnalysis(analysisId);
}

export async function createAnalysisTemplate(
  template: AnalysisTemplateDAO.CreateAnalysisTemplateParams
): Promise<void> {
  const createAnalysisTemplateResponse = await AnalysisTemplateDAO.createAnalysisTemplate(template);
  await refreshTemplateSelectorQuery('network-only');

  assert(
    createAnalysisTemplateResponse.createdTemplates.length === 1,
    'Expected exactly 1 template to be created'
  );

  navigateToURL(
    AnalysisTemplateDAO.analysisTemplatePageUrl(createAnalysisTemplateResponse.createdTemplates[0].createdId)
  );
}

export async function copyAnalysisTemplate(
  copyParams: AnalysisTemplateDAO.CopyAnalysisTemplateParams
): Promise<void> {
  const createAnalysisTemplateResponse = await AnalysisTemplateDAO.copyAnalysisTemplate(copyParams);
  await refreshTemplateSelectorQuery('network-only');

  navigateToURL(
    AnalysisTemplateDAO.analysisTemplatePageUrl(createAnalysisTemplateResponse.newAnalysisTemplateId)
  );
}

export async function createEmptyAnalysisTemplate(name: string, templateCollectionId: string): Promise<void> {
  const createAnalysisTemplateResponse = await AnalysisTemplateDAO.createAnalysisTemplate({
    name,
    createdFromPipelineId: null,
    dataFrameVariables: [],
    templateCollectionId,
    outputSteps: [],
  });
  await refreshTemplateSelectorQuery('network-only');

  assert(
    createAnalysisTemplateResponse.createdTemplates.length === 1,
    'Expected exactly 1 template to be created'
  );

  navigateToURL(
    AnalysisTemplateDAO.analysisTemplatePageUrl(createAnalysisTemplateResponse.createdTemplates[0].createdId)
  );
}

export async function moveAnalysisTemplateCollection(
  analysisTemplate: AnalysisTemplateGraphQL,
  templateCollectionId: string
): Promise<void> {
  const {updatedIds} = await AnalysisTemplateDAO.updateAnalysisTemplate({
    id: analysisTemplate.id,
    templateCollectionId,
  });
  assert(updatedIds.length === 1, 'Expected 1 updated analysis template to be returned');

  navigateToURL(TemplateCollectionDAO.collectionURL(templateCollectionId));
  navigateToURL(AnalysisTemplateDAO.analysisTemplatePageUrl(updatedIds[0]));

  await refreshAnalysisTemplate(updatedIds[0]);

  notifications.success('Analysis template was moved succesfully.');
}

export async function publishAnalysisTemplate(analysisTemplate: AnalysisTemplateGraphQL): Promise<void> {
  const {updatedIds} = await AnalysisTemplateDAO.updateAnalysisTemplate({
    id: analysisTemplate.id,
    shouldPublish: true,
  });
  assert(updatedIds.length === 1, 'Expected 1 updated analysis template to be returned');

  navigateToURL(AnalysisTemplateDAO.analysisTemplatePageUrl(updatedIds[0]));

  await refreshAnalysisTemplate(updatedIds[0]);

  notifications.success('Analysis template was published succesfully.');
}

export async function renameAnalysisTemplate(
  analysisTemplate: AnalysisTemplateGraphQL,
  name: string
): Promise<void> {
  const {updatedIds} = await AnalysisTemplateDAO.updateAnalysisTemplate({
    id: analysisTemplate.id,
    name,
  });

  assert(updatedIds.length === 1, 'Expected 1 updated analysis template to be returned');

  await refreshAnalysisTemplate(updatedIds[0]);
}
